import { useCountdown } from '@femtasy/ui';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { isFlashSaleActive } from 'helpers/flashSale';

import { useAuth } from 'hooks/useAuth';

import { promoCountdownEndsAt } from 'store/modules/promoCountdownBanner';
import ReduxState from 'store/ReduxState';

const ID = '[^/]+';
const RESOURCE_ID = `(?!subscription$)[^/]+`;
const STORY_DETAILS = `(/${ID}/details)?`;

export const PROMO_COUNTDOWN_ALLOWED_PATHNAMES = [
  new RegExp(
    `^/browse(/mood/${ID}|/tag/${ID}|/speakers/${ID}/stories)?${STORY_DETAILS}$`,
  ),
  new RegExp(
    `^/explore(/tag/${ID}|/scenarios|/guided-sessions|/voices|/kinks|/lgbtqia|/self-love|/360-audios|/sounds|/all)?${STORY_DETAILS}$`,
  ),
  new RegExp(
    `^/stories(/tag/${ID}|/speakers/${ID}/stories)?${STORY_DETAILS}$`,
  ),
  new RegExp(
    `^/playlists(/tag/${ID}|/speakers/${ID}/stories|/recently-listened|/liked-audios|/${RESOURCE_ID})?${STORY_DETAILS}$`,
  ),
  new RegExp(`^/360audios(/tag/${ID})?${STORY_DETAILS}$`),
  new RegExp(`^/amorelie${STORY_DETAILS}$`),
  new RegExp(`^/speakers/${ID}/stories${STORY_DETAILS}$`),
  new RegExp(`^/search(/results|/story/tag/${ID}|/story/${ID}/details)?$`),
  new RegExp(`^/journeys(/${RESOURCE_ID})?$`),
];

const normalizePathname = (pathname: string) =>
  pathname
    .replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '')
    .replace(/\/+$/, '');

export const isPromoCountdownAllowedPathname = (pathname: string) => {
  const normalizedPathname = normalizePathname(pathname);

  return PROMO_COUNTDOWN_ALLOWED_PATHNAMES.some((allowed) =>
    allowed.test(normalizedPathname),
  );
};

export const usePromoCountdownBannerVisibility = () => {
  const { hasPremiumAccess, isAuthenticated, user } = useAuth();
  const { locale } = useIntl();
  const { pathname } = useLocation();

  const endsAt = useSelector<ReduxState, number | undefined>(
    promoCountdownEndsAt,
  );

  const isAuthProfilePending = isAuthenticated && !user;
  const { timeLeft } = useCountdown({ endsAt: endsAt ?? 0 });

  const isVisible =
    !isAuthProfilePending &&
    !hasPremiumAccess &&
    !isFlashSaleActive(locale) &&
    !!timeLeft &&
    isPromoCountdownAllowedPathname(pathname);

  return {
    isVisible,
    timeLeft,
  };
};
